import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  MessageSquare, Megaphone, Plus, MoreVertical, Send, Eye, Trash2,
  Bug, Lightbulb, Sparkles, HelpCircle, Loader2, Search, Filter,
  CheckCircle, Clock, ThumbsUp, Edit, Pin, PinOff, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const feedbackTypeIcons = {
  bug: Bug,
  feature_request: Lightbulb,
  improvement: Sparkles,
  general: HelpCircle
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  planned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800'
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800'
};

const releaseTypeColors = {
  new_feature: 'bg-green-100 text-green-800',
  improvement: 'bg-blue-100 text-blue-800',
  bug_fix: 'bg-yellow-100 text-yellow-800',
  security: 'bg-red-100 text-red-800',
  announcement: 'bg-purple-100 text-purple-800'
};

const AdminFeedbackManagement = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('feedback');
  const [loading, setLoading] = useState(false);
  
  // Feedback state
  const [allFeedback, setAllFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [isPublicResponse, setIsPublicResponse] = useState(true);
  const [responding, setResponding] = useState(false);
  
  // Release notes state
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [showNewRelease, setShowNewRelease] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newRelease, setNewRelease] = useState({
    title: '',
    content: '',
    note_type: 'announcement',
    version: '',
    is_published: false,
    is_pinned: false,
    tags: []
  });
  const [savingRelease, setSavingRelease] = useState(false);

  useEffect(() => {
    if (activeTab === 'feedback') {
      loadFeedback();
      loadFeedbackStats();
    } else {
      loadReleaseNotes();
    }
  }, [activeTab, statusFilter, typeFilter, searchQuery]);

  const loadFeedback = async () => {
    setFeedbackLoading(true);
    try {
      let url = `${API_URL}/api/feedback/admin/all?page=1&limit=100`;
      if (statusFilter) url += `&status_filter=${statusFilter}`;
      if (typeFilter) url += `&type_filter=${typeFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllFeedback(data.feedback || []);
      }
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const loadFeedbackStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/feedback/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFeedbackStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadReleaseNotes = async () => {
    setReleasesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback/release-notes?published_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReleaseNotes(data.release_notes || []);
      }
    } catch (error) {
      console.error('Failed to load release notes:', error);
    } finally {
      setReleasesLoading(false);
    }
  };

  const handleUpdateFeedbackStatus = async (feedbackId, status, priority = null) => {
    try {
      const body = { status };
      if (priority) body.priority = priority;
      
      const res = await fetch(`${API_URL}/api/feedback/admin/${feedbackId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        toast.success('Feedback updated');
        loadFeedback();
        loadFeedbackStats();
      }
    } catch (error) {
      toast.error('Failed to update feedback');
    }
  };

  const handleRespondToFeedback = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }

    setResponding(true);
    try {
      const res = await fetch(`${API_URL}/api/feedback/admin/${selectedFeedback.id}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          response_text: responseText,
          is_public: isPublicResponse
        })
      });

      if (res.ok) {
        toast.success('Response added');
        setResponseText('');
        setSelectedFeedback(null);
        loadFeedback();
      }
    } catch (error) {
      toast.error('Failed to add response');
    } finally {
      setResponding(false);
    }
  };

  const handleSaveRelease = async () => {
    if (!newRelease.title.trim() || !newRelease.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    setSavingRelease(true);
    try {
      const isEdit = !!editingNote;
      const url = isEdit 
        ? `${API_URL}/api/feedback/release-notes/${editingNote.id}`
        : `${API_URL}/api/feedback/release-notes`;
      
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newRelease)
      });

      if (res.ok) {
        toast.success(isEdit ? 'Release note updated' : 'Release note created');
        setShowNewRelease(false);
        setEditingNote(null);
        setNewRelease({
          title: '',
          content: '',
          note_type: 'announcement',
          version: '',
          is_published: false,
          is_pinned: false,
          tags: []
        });
        loadReleaseNotes();
      }
    } catch (error) {
      toast.error('Failed to save release note');
    } finally {
      setSavingRelease(false);
    }
  };

  const handleDeleteRelease = async (noteId) => {
    if (!confirm('Are you sure you want to delete this release note?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/feedback/release-notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Release note deleted');
        loadReleaseNotes();
      }
    } catch (error) {
      toast.error('Failed to delete release note');
    }
  };

  const handleTogglePublish = async (note) => {
    try {
      const res = await fetch(`${API_URL}/api/feedback/release-notes/${note.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_published: !note.is_published })
      });
      
      if (res.ok) {
        toast.success(note.is_published ? 'Unpublished' : 'Published');
        loadReleaseNotes();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      const res = await fetch(`${API_URL}/api/feedback/release-notes/${note.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_pinned: !note.is_pinned })
      });
      
      if (res.ok) {
        toast.success(note.is_pinned ? 'Unpinned' : 'Pinned');
        loadReleaseNotes();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const openEditRelease = (note) => {
    setEditingNote(note);
    setNewRelease({
      title: note.title,
      content: note.content,
      note_type: note.note_type,
      version: note.version || '',
      is_published: note.is_published,
      is_pinned: note.is_pinned,
      tags: note.tags || []
    });
    setShowNewRelease(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const navigate = useNavigate();

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="admin-feedback-page">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Feedback & Release Notes</h1>
          <p className="text-gray-600 mt-1">
            Manage vendor feedback and publish platform updates
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="feedback" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Vendor Feedback
              {feedbackStats?.by_status?.pending > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {feedbackStats.by_status.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="releases" className="flex items-center gap-2">
              <Megaphone className="h-4 w-4" />
              Release Notes
            </TabsTrigger>
          </TabsList>

          {/* Feedback Management Tab */}
          <TabsContent value="feedback" className="space-y-6">
            {/* Stats Cards */}
            {feedbackStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">{feedbackStats.total}</div>
                    <div className="text-sm text-gray-500">Total Feedback</div>
                  </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-yellow-600">
                    {feedbackStats.by_status?.pending || 0}
                  </div>
                  <div className="text-sm text-gray-500">Pending Review</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-600">
                    {feedbackStats.by_type?.feature_request || 0}
                  </div>
                  <div className="text-sm text-gray-500">Feature Requests</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-red-600">
                    {feedbackStats.by_type?.bug || 0}
                  </div>
                  <div className="text-sm text-gray-500">Bug Reports</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search feedback..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter || "all"} onValueChange={(val) => setTypeFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="bug">Bug Report</SelectItem>
                <SelectItem value="feature_request">Feature Request</SelectItem>
                <SelectItem value="improvement">Improvement</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Feedback List */}
          {feedbackLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : allFeedback.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Feedback Found</h3>
                <p className="text-gray-500 mt-1">No feedback matches your filters</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {allFeedback.map((fb) => {
                const TypeIcon = feedbackTypeIcons[fb.feedback_type] || HelpCircle;
                return (
                  <Card key={fb.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <TypeIcon className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-gray-900">{fb.title}</h3>
                            <Badge className={statusColors[fb.status]}>
                              {fb.status?.replace('_', ' ')}
                            </Badge>
                            {fb.priority && (
                              <Badge className={priorityColors[fb.priority]}>
                                {fb.priority}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{fb.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>From: {fb.vendor_name}</span>
                            <span>{formatDate(fb.created_at)}</span>
                            {fb.feedback_type === 'feature_request' && (
                              <span className="flex items-center gap-1">
                                <ThumbsUp className="h-3 w-3" />
                                {fb.upvotes || 0} votes
                              </span>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setSelectedFeedback(fb)}>
                              <Send className="h-4 w-4 mr-2" />
                              Respond
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateFeedbackStatus(fb.id, 'under_review')}>
                              <Eye className="h-4 w-4 mr-2" />
                              Mark Under Review
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateFeedbackStatus(fb.id, 'planned')}>
                              <Clock className="h-4 w-4 mr-2" />
                              Mark Planned
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateFeedbackStatus(fb.id, 'in_progress')}>
                              <Loader2 className="h-4 w-4 mr-2" />
                              Mark In Progress
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateFeedbackStatus(fb.id, 'completed')}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark Completed
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Release Notes Tab */}
        <TabsContent value="releases" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">All Release Notes</h2>
            <Button onClick={() => { setEditingNote(null); setShowNewRelease(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              New Release Note
            </Button>
          </div>

          {releasesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : releaseNotes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Megaphone className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No Release Notes</h3>
                <p className="text-gray-500 mt-1">Create your first release note to keep vendors updated</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {releaseNotes.map((note) => (
                <Card key={note.id} className={note.is_pinned ? 'border-blue-300' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {note.is_pinned && (
                            <Badge variant="outline" className="text-blue-600 border-blue-300">
                              <Pin className="h-3 w-3 mr-1" />
                              Pinned
                            </Badge>
                          )}
                          <Badge className={note.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {note.is_published ? 'Published' : 'Draft'}
                          </Badge>
                          <Badge className={releaseTypeColors[note.note_type]}>
                            {note.note_type?.replace('_', ' ')}
                          </Badge>
                          {note.version && (
                            <span className="text-sm text-gray-500">v{note.version}</span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900">{note.title}</h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{note.content}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>By {note.created_by_name}</span>
                          <span>{formatDate(note.created_at)}</span>
                          {note.is_published && (
                            <span>{note.views_count || 0} views</span>
                          )}
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditRelease(note)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleTogglePublish(note)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {note.is_published ? 'Unpublish' : 'Publish'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleTogglePin(note)}>
                            {note.is_pinned ? <PinOff className="h-4 w-4 mr-2" /> : <Pin className="h-4 w-4 mr-2" />}
                            {note.is_pinned ? 'Unpin' : 'Pin'}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteRelease(note.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Respond to Feedback Dialog */}
      <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Respond to Feedback</DialogTitle>
            <DialogDescription>
              {selectedFeedback?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              {selectedFeedback?.description}
            </div>
            <div className="space-y-2">
              <Label>Your Response</Label>
              <Textarea
                placeholder="Type your response..."
                rows={4}
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is-public"
                checked={isPublicResponse}
                onChange={(e) => setIsPublicResponse(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="is-public" className="text-sm font-normal">
                Make this response visible to the vendor
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedFeedback(null)}>
              Cancel
            </Button>
            <Button onClick={handleRespondToFeedback} disabled={responding}>
              {responding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Release Note Dialog */}
      <Dialog open={showNewRelease} onOpenChange={setShowNewRelease}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNote ? 'Edit Release Note' : 'Create Release Note'}</DialogTitle>
            <DialogDescription>
              Publish updates and announcements for vendors
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newRelease.note_type}
                  onValueChange={(val) => setNewRelease({ ...newRelease, note_type: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_feature">New Feature</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                    <SelectItem value="bug_fix">Bug Fix</SelectItem>
                    <SelectItem value="security">Security Update</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Version (Optional)</Label>
                <Input
                  placeholder="e.g., 2.5.0"
                  value={newRelease.version}
                  onChange={(e) => setNewRelease({ ...newRelease, version: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Release note title"
                value={newRelease.title}
                onChange={(e) => setNewRelease({ ...newRelease, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                placeholder="Describe the changes, new features, or announcements..."
                rows={8}
                value={newRelease.content}
                onChange={(e) => setNewRelease({ ...newRelease, content: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-published"
                  checked={newRelease.is_published}
                  onChange={(e) => setNewRelease({ ...newRelease, is_published: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is-published" className="text-sm font-normal">
                  Publish immediately
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-pinned"
                  checked={newRelease.is_pinned}
                  onChange={(e) => setNewRelease({ ...newRelease, is_pinned: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is-pinned" className="text-sm font-normal">
                  Pin to top
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRelease(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRelease} disabled={savingRelease}>
              {savingRelease && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingNote ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminFeedbackManagement;
